import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { CircleDollarSign, ShieldCheck } from 'lucide-react';
import AppLayout from '../components/layout/AppLayout';
import { useTimezone, PAGE } from '@kioskgaming/ui';
import { formatInTimezone } from '@kioskgaming/ui';
import PortalEmptyState from '../components/ui/PortalEmptyState';
import { approveRedemption, fetchRedemptionApprovals, rejectRedemption } from '../services/api';
import type { CashierRedemptionApproval } from '../types';

function formatMoney(amount: string | number): string {
  return `$${Number(amount).toFixed(2)}`;
}

export default function RedemptionApprovalsPage() {
  const { timezone } = useTimezone();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<CashierRedemptionApproval[]>([]);
  const [query, setQuery] = useState('');
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<CashierRedemptionApproval | null>(null);
  const [rejectNote, setRejectNote] = useState('');

  const getApiErrorMessage = (e: unknown) => {
    const anyE = e as { response?: { data?: { message?: string } }; message?: string };
    return anyE?.response?.data?.message || anyE?.message || null;
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchRedemptionApprovals();
      if (!res.success) throw new Error(res.message || 'Failed to load approvals');
      setRows(res.data || []);
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Failed to load approvals');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const id = String(r.id || '').toLowerCase();
      const txId = String(r.cashierTransactionId || '').toLowerCase();
      const amt = String(r.amount || '');
      return `${id} ${txId} ${amt}`.includes(q);
    });
  }, [rows, query]);

  const approve = async (row: CashierRedemptionApproval) => {
    setApprovingId(row.id);
    try {
      const res = await approveRedemption(row.id);
      if (!res.success) throw new Error(res.message || 'Approve failed');
      toast.success('Redemption approved');
      await load();
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Approve failed');
    } finally {
      setApprovingId(null);
    }
  };

  const openRejectModal = (row: CashierRedemptionApproval) => {
    setRejectTarget(row);
    setRejectNote('');
    setRejectModalOpen(true);
  };

  const closeRejectModal = () => {
    if (rejectingId) return;
    setRejectModalOpen(false);
    setRejectTarget(null);
    setRejectNote('');
  };

  const submitReject = async () => {
    const row = rejectTarget;
    if (!row) return;
    setRejectingId(row.id);
    try {
      const res = await rejectRedemption(row.id, rejectNote.trim() ? { note: rejectNote.trim() } : undefined);
      if (!res.success) throw new Error(res.message || 'Reject failed');
      toast.success('Redemption rejected');
      closeRejectModal();
      await load();
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Reject failed');
    } finally {
      setRejectingId(null);
    }
  };

  return (
    <AppLayout contentFullWidth>
      <div className="w-full max-w-none">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">{PAGE.redemptionApprovals}</h1>
            <p className="mt-0.5 text-sm text-stone-600">
              Review high-value or daily-limit redemptions before credits are paid out.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="search"
              placeholder="Search by ID or amount…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-500 sm:w-64"
            />
            <button
              type="button"
              className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
              onClick={() => void load()}
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-amber-700">Pending approvals</div>
          <div className="mt-1 text-2xl font-bold text-amber-900">{rows.length}</div>
        </div>

        <div className="mt-4 rounded-xl border border-stone-200 bg-white">
          {loading ? (
            <div className="px-4 py-8 text-sm text-stone-600">Loading…</div>
          ) : filtered.length ? (
            <div className="divide-y divide-stone-100">
              {filtered.map((row) => (
                <div key={row.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <CircleDollarSign className="h-4 w-4 text-stone-500" />
                      <span className="text-lg font-bold text-stone-900">{formatMoney(row.amount)}</span>
                    </div>
                    <div className="mt-1 text-xs text-stone-600">
                      Requested {formatInTimezone(row.createdAt, timezone)}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-stone-500">
                      Tx: {row.cashierTransactionId.slice(0, 8)}…
                    </div>
                    {row.redeemConsent ? (
                      <div className="mt-1 inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                        <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
                        {row.redeemConsent.via === 'app' ? 'Player approved in the app' : 'Player approved with code'}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      disabled={approvingId === row.id || rejectingId === row.id}
                      className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                      onClick={() => void approve(row)}
                    >
                      {approvingId === row.id ? 'Approving…' : 'Approve'}
                    </button>
                    <button
                      type="button"
                      disabled={approvingId === row.id || rejectingId === row.id}
                      className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60"
                      onClick={() => openRejectModal(row)}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <PortalEmptyState
              icon={<CircleDollarSign className="mx-auto h-12 w-12 text-stone-400" strokeWidth={1.5} />}
              title="No pending redemptions"
              description="Redemptions that need your approval will appear here."
            />
          )}
        </div>
      </div>

      {rejectModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <div className="text-lg font-semibold">Reject redemption</div>
            <p className="mt-1 text-sm text-stone-600">
              Reject {rejectTarget ? formatMoney(rejectTarget.amount) : ''} redemption request?
            </p>
            <div className="mt-4">
              <label className="text-sm font-medium text-stone-700">Note (optional)</label>
              <textarea
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                rows={3}
                className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-emerald-500"
                placeholder="Reason for rejection…"
              />
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
                onClick={closeRejectModal}
                disabled={!!rejectingId}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!!rejectingId}
                className="rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
                onClick={() => void submitReject()}
              >
                {rejectingId ? 'Rejecting…' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AppLayout>
  );
}
